import React from 'react';
import {
  Hash,
  Send,
  Smile,
  Paperclip,
  Search,
  Settings,
  Plus,
} from 'lucide-react';
import type { ThemeTokens } from '../../types/theme';

interface LiveThemePreviewProps {
  tokens: ThemeTokens;
}

export const LiveThemePreview: React.FC<LiveThemePreviewProps> = ({ tokens }) => {
  return (
    <div
      className="w-full rounded-2xl border overflow-hidden shadow-xl select-none text-xs transition-colors duration-200 flex flex-col font-sans"
      style={{
        backgroundColor: tokens.appBackground,
        borderColor: tokens.borderColor,
      }}
    >
      {/* Mini App Window Header */}
      <div
        className="px-3.5 py-2 border-b flex items-center justify-between"
        style={{
          backgroundColor: tokens.headerBackground,
          borderColor: tokens.sidebarBorder,
          color: tokens.sidebarText,
        }}
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400/80 inline-block" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80 inline-block" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80 inline-block" />
          </div>
          <span className="font-bold text-[11px] ml-1 opacity-90">Sahaj Connect — Live Theme Preview</span>
        </div>

        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-1 px-2 py-0.5 rounded-lg border text-[10px]"
            style={{
              backgroundColor: tokens.inputBackground,
              borderColor: tokens.inputBorder,
              color: tokens.sidebarSecondaryText,
            }}
          >
            <Search className="h-2.5 w-2.5" style={{ color: tokens.iconColor }} />
            <span>Search workspace...</span>
          </div>

          <div
            className="w-5 h-5 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: tokens.primaryAccent, color: tokens.primaryButtonText }}
          >
            <span className="text-[10px] font-bold">SC</span>
          </div>
        </div>
      </div>

      {/* App Body Grid */}
      <div className="flex h-72 w-full overflow-hidden">
        {/* 1. Mini Workspace Rail */}
        <div
          className="w-11 border-r flex flex-col items-center py-2.5 justify-between flex-shrink-0"
          style={{
            backgroundColor: tokens.miniSidebarBackground,
            borderColor: tokens.sidebarBorder,
          }}
        >
          <div className="flex flex-col items-center gap-2">
            {/* Active workspace icon */}
            <div
              className="w-7 h-7 rounded-xl flex items-center justify-center text-white font-bold text-xs shadow-xs ring-2"
              style={{
                backgroundColor: tokens.primaryAccent,
                borderColor: tokens.miniSidebarBackground,
              }}
            >
              H
            </div>

            {/* Plus workspace icon */}
            <div
              className="w-7 h-7 rounded-xl border border-dashed flex items-center justify-center"
              style={{
                borderColor: tokens.sidebarSecondaryText,
                color: tokens.sidebarSecondaryText,
              }}
            >
              <Plus className="h-3 w-3" />
            </div>
          </div>

          <div className="flex flex-col items-center gap-2">
            <Settings className="h-3.5 w-3.5" style={{ color: tokens.sidebarSecondaryText }} />
            <div
              className="w-6 h-6 rounded-lg flex items-center justify-center font-bold text-[10px] text-white relative"
              style={{ backgroundColor: '#F97316' }}
            >
              J
              <span
                className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border"
                style={{
                  backgroundColor: tokens.statusOnline,
                  borderColor: tokens.miniSidebarBackground,
                }}
              />
            </div>
          </div>
        </div>

        {/* 2. Primary Channels Sidebar */}
        <div
          className="w-40 border-r flex flex-col p-2.5 flex-shrink-0 overflow-y-auto"
          style={{
            backgroundColor: tokens.primarySidebarBackground,
            borderColor: tokens.sidebarBorder,
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-[11px] truncate" style={{ color: tokens.sidebarText }}>
              Sahaj Connect
            </span>
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: tokens.statusOnline }}
            />
          </div>

          {/* Section: Channels */}
          <div className="space-y-0.5 mb-3">
            <div className="text-[9px] font-bold uppercase tracking-wider mb-1" style={{ color: tokens.sidebarSecondaryText }}>
              Channels
            </div>

            {/* Active Channel */}
            <div
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg font-semibold text-[11px]"
              style={{
                backgroundColor: tokens.sidebarActiveBackground,
                color: tokens.sidebarActiveText,
              }}
            >
              <Hash className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">general</span>
            </div>

            {/* Inactive Channel with hover preview */}
            <div
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px]"
              style={{
                color: tokens.sidebarText,
              }}
            >
              <Hash className="h-3 w-3 flex-shrink-0" style={{ color: tokens.sidebarSecondaryText }} />
              <span className="truncate">engineering</span>
            </div>

            <div
              className="flex items-center justify-between px-2 py-1 rounded-lg text-[11px]"
              style={{
                color: tokens.sidebarText,
              }}
            >
              <div className="flex items-center gap-1.5 truncate">
                <Hash className="h-3 w-3 flex-shrink-0" style={{ color: tokens.sidebarSecondaryText }} />
                <span className="truncate">announcements</span>
              </div>
              <span
                className="text-[9px] px-1 py-0.2 rounded-full font-bold"
                style={{
                  backgroundColor: tokens.primaryAccent,
                  color: tokens.primaryButtonText,
                }}
              >
                3
              </span>
            </div>
          </div>

          {/* Section: Direct Messages */}
          <div className="space-y-0.5">
            <div className="text-[9px] font-bold uppercase tracking-wider mb-1" style={{ color: tokens.sidebarSecondaryText }}>
              Direct Messages
            </div>

            <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px]" style={{ color: tokens.sidebarText }}>
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tokens.statusOnline }} />
              <span className="truncate">Alex Rivers</span>
            </div>

            <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px]" style={{ color: tokens.sidebarText }}>
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tokens.statusAway }} />
              <span className="truncate">Dev Team</span>
            </div>
          </div>
        </div>

        {/* 3. Main Chat View */}
        <div
          className="flex-1 flex flex-col justify-between overflow-hidden"
          style={{
            backgroundColor: tokens.chatBackground,
          }}
        >
          {/* Channel Bar */}
          <div
            className="px-3 py-1.5 border-b flex items-center justify-between flex-shrink-0"
            style={{
              backgroundColor: tokens.headerBackground,
              borderColor: tokens.borderColor,
            }}
          >
            <div className="flex items-center gap-1.5">
              <Hash className="h-3.5 w-3.5" style={{ color: tokens.primaryAccent }} />
              <span className="font-bold text-[11px]" style={{ color: tokens.messageText }}>
                general
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-md"
                style={{
                  backgroundColor: tokens.cardBackground,
                  color: tokens.messageSecondaryText,
                  borderColor: tokens.borderColor,
                }}
              >
                8 members
              </span>
            </div>
          </div>

          {/* Messages Stream */}
          <div className="flex-1 p-3 space-y-2.5 overflow-y-auto">
            {/* Incoming Message */}
            <div
              className="flex items-start gap-2 p-2 rounded-xl border transition-all"
              style={{
                backgroundColor: tokens.incomingMessageBackground,
                borderColor: tokens.borderColor,
              }}
            >
              <div
                className="w-6 h-6 rounded-lg flex items-center justify-center font-bold text-[10px] text-white flex-shrink-0 mt-0.5"
                style={{ backgroundColor: '#3B82F6' }}
              >
                A
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="font-bold text-[11px]" style={{ color: tokens.messageText }}>
                    Alex Rivers
                  </span>
                  <span className="text-[9px]" style={{ color: tokens.messageTimestamp }}>
                    10:42 AM
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: tokens.messageText }}>
                  Hey team! Have you checked out the new color customization engine?
                </p>

                {/* Inline Code snippet */}
                <div className="mt-1.5 flex items-center gap-1">
                  <span
                    className="px-1.5 py-0.5 rounded font-mono text-[10px] font-semibold"
                    style={{
                      backgroundColor: tokens.inlineCodeBackground,
                      color: tokens.inlineCodeText,
                    }}
                  >
                    --theme-primary: {tokens.primaryAccent};
                  </span>
                </div>
              </div>
            </div>

            {/* Outgoing Message with Mention & Reaction */}
            <div
              className="flex items-start gap-2 p-2 rounded-xl border transition-all"
              style={{
                backgroundColor: tokens.outgoingMessageBackground,
                borderColor: tokens.borderColor,
              }}
            >
              <div
                className="w-6 h-6 rounded-lg flex items-center justify-center font-bold text-[10px] text-white flex-shrink-0 mt-0.5"
                style={{ backgroundColor: '#F97316' }}
              >
                J
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="font-bold text-[11px]" style={{ color: tokens.messageText }}>
                    You
                  </span>
                  <span className="text-[9px]" style={{ color: tokens.messageTimestamp }}>
                    10:43 AM
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: tokens.messageText }}>
                  Yes{' '}
                  <span
                    className="px-1.5 py-0.5 rounded-md font-semibold text-[10px]"
                    style={{
                      backgroundColor: tokens.mentionBackground,
                      color: tokens.mentionText,
                    }}
                  >
                    @Alex
                  </span>
                  , it looks incredible! Tested across all UI themes.
                </p>

                {/* Reactions */}
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold"
                    style={{
                      backgroundColor: tokens.reactionActiveBackground,
                      borderColor: tokens.reactionActiveBorder,
                      color: tokens.primaryAccent,
                    }}
                  >
                    🔥 3
                  </span>
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px]"
                    style={{
                      backgroundColor: tokens.reactionBackground,
                      borderColor: tokens.reactionBorder,
                      color: tokens.messageSecondaryText,
                    }}
                  >
                    ✨ 2
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Composer */}
          <div
            className="p-2 border-t flex-shrink-0"
            style={{
              backgroundColor: tokens.chatBackground,
              borderColor: tokens.borderColor,
            }}
          >
            <div
              className="flex items-center gap-1.5 p-1.5 rounded-xl border shadow-2xs"
              style={{
                backgroundColor: tokens.composerBackground,
                borderColor: tokens.composerBorder,
              }}
            >
              <Paperclip className="h-3.5 w-3.5 flex-shrink-0" style={{ color: tokens.iconColor }} />
              <input
                type="text"
                readOnly
                placeholder={`Message #general...`}
                className="w-full bg-transparent text-[11px] focus:outline-none"
                style={{
                  color: tokens.composerText,
                }}
              />
              <Smile className="h-3.5 w-3.5 flex-shrink-0" style={{ color: tokens.iconColor }} />
              <button
                type="button"
                className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 shadow-xs"
                style={{
                  backgroundColor: tokens.primaryButtonBackground,
                  color: tokens.primaryButtonText,
                }}
              >
                <Send className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
